import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { useRef } from 'react'
import { useAuthToken } from 'hooks/useAuthToken'
import toast from 'react-hot-toast'

const apiUrl = process.env.REACT_APP_TLE_API_REST || ''

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

const ToolbarButton: React.FC<{
  onClick: () => void
  active?: boolean
  title: string
  children: React.ReactNode
}> = ({ onClick, active, title, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`px-2 py-1 text-sm border rounded ${
      active ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
)

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [authToken] = useAuthToken()
  const editorRef = useRef<ReturnType<typeof useEditor>>(null)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    }
  })

  // Keep a stable ref so the async upload callback always has the current editor
  editorRef.current = editor

  const uploadImage = async (file: File) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await fetch(`${apiUrl}/images/upload`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${authToken}` }
      })
      if (!response.ok) throw new Error('Upload failed')
      const image = await response.json()
      if (image?.url && editorRef.current) {
        editorRef.current.chain().focus().setImage({ src: image.url }).run()
      }
    } catch {
      toast.error('Could not upload image')
    }
  }

  if (!editor) return null

  return (
    <div className="border border-gray-300 rounded">
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50">
        <ToolbarButton
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
        >
          <u>U</u>
        </ToolbarButton>
        <span className="w-px bg-gray-300 mx-1" />
        <ToolbarButton
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
        >
          H3
        </ToolbarButton>
        <span className="w-px bg-gray-300 mx-1" />
        <ToolbarButton
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          title="Ordered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          1. List
        </ToolbarButton>
        <span className="w-px bg-gray-300 mx-1" />
        <ToolbarButton
          title="Insert table"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          Table
        </ToolbarButton>
        <ToolbarButton title="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()}>
          +Col
        </ToolbarButton>
        <ToolbarButton title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()}>
          -Col
        </ToolbarButton>
        <ToolbarButton title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}>
          +Row
        </ToolbarButton>
        <ToolbarButton title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>
          -Row
        </ToolbarButton>
        <span className="w-px bg-gray-300 mx-1" />
        <ToolbarButton title="Insert image" onClick={() => fileInputRef.current?.click()}>
          Image
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) uploadImage(file)
            e.target.value = ''
          }}
        />
        <span className="w-px bg-gray-300 mx-1" />
        <ToolbarButton
          title="Align left"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
        >
          ←
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
        >
          ↔
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
        >
          →
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="p-4 min-h-[400px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[400px] [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:my-3 [&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-bold [&_.ProseMirror_h3]:my-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-2 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-2 [&_.ProseMirror_li]:my-0.5 [&_.ProseMirror_p]:my-1 [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-gray-300 [&_.ProseMirror_td]:p-2 [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-gray-300 [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-gray-100 [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:my-2"
      />
    </div>
  )
}
